import DescriptionIcon from "@mui/icons-material/Description";
import PeopleIcon from "@mui/icons-material/People";
import {
	Box,
	Collapse,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	styled,
	Typography,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import type React from "react";
import { useMemo } from "react";
import { Link, useLocation } from "react-router";
import {
	HEADER_HEIGHT,
	NAVIGATION_HEIGHT,
	SIDEMENU_WIDTH,
	TERM_ICON_PATH,
} from "@/config/constants";
import { useAuth } from "@/features/auth";
import type { ContentType } from "@/shared/types";

interface SideMenuProps {
	selectedContent?: ContentType;
}

interface SubItem {
	text: React.ReactNode;
	icon: React.ReactElement;
	path: string;
}
interface MenuItem {
	name: React.ReactNode;
	icon: React.ReactElement;
	subItems?: SubItem[];
}

const Drawer = styled(MuiDrawer)(() => ({
	width: SIDEMENU_WIDTH,
	flexShrink: 0,
	whiteSpace: "nowrap",
	boxSizing: "border-box",
	justifyContent: "space-between",
	"& .MuiDrawer-paper": {
		width: SIDEMENU_WIDTH,
		height: `calc(100vh - ${HEADER_HEIGHT}px - ${NAVIGATION_HEIGHT}px)`,
		marginTop: `${HEADER_HEIGHT + NAVIGATION_HEIGHT}px`,
		overflowX: "hidden",
	},
}));

type MenuContentType = Exclude<ContentType, null>;

const menuData: Record<MenuContentType, MenuItem[]> = {
	お知らせ: [
		{
			name: "新規作成",
			icon: <DescriptionIcon />,
			subItems: [
				{
					text: "新規作成",
					icon: <DescriptionIcon />,
					path: "/informations/new",
				},
			],
		},
		{
			name: "配信済みのお知らせ一覧",
			icon: <DescriptionIcon />,
			subItems: [
				{
					text: "配信済みのお知らせ一覧",
					icon: <DescriptionIcon />,
					path: "/informations",
				},
			],
		},
	],
	利用規約: [
		{
			name: (
				<span>
					Glicoメンバーズ
					<br />
					利用規約
				</span>
			),
			icon: <DescriptionIcon />,
			subItems: [
				{
					text: (
						<span>
							Glicoメンバーズ
							<br />
							利用規約
						</span>
					),
					icon: <DescriptionIcon />,
					path: "/terms/glico-members",
				},
			],
		},
		{
			name: (
				<span>
					こぺ
					<br />
					利用規約
				</span>
			),
			icon: <DescriptionIcon />,
			subItems: [
				{
					text: (
						<span>
							こぺ
							<br />
							利用規約
						</span>
					),
					icon: <DescriptionIcon />,
					path: "/terms/app",
				},
			],
		},
	],
	ユーザー管理: [
		{
			name: "ユーザー管理",
			icon: <PeopleIcon />,
			subItems: [
				{
					text: "ユーザー管理",
					icon: <PeopleIcon />,
					path: "/admins",
				},
			],
		},
	],
};

export function SideMenu({ selectedContent }: SideMenuProps): React.ReactElement {
	const { usergroup } = useAuth();
	const location = useLocation();
	const pathname = location.pathname;
	const contentKey: MenuContentType = selectedContent ?? "利用規約";

	// 実行者の場合、「新規作成」を削除
	const filteredMenuItems = useMemo(() => {
		const items = menuData[contentKey] ?? [];
		if (usergroup !== "管理者") {
			return items.filter((item) => item.name !== "新規作成");
		}
		return items;
	}, [contentKey, usergroup]);

	const imageMap: Record<string, string> = {
		お知らせ: TERM_ICON_PATH,
		利用規約: TERM_ICON_PATH,
	};

	const imageSrc: string | null = selectedContent ? (imageMap[selectedContent] ?? null) : null;

	return (
		<Drawer variant="permanent" sx={{ gridArea: "sidemenu" }}>
			<Divider />
			{imageSrc && selectedContent && (
				<Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
					<img src={imageSrc} alt={selectedContent} style={{ width: "auto", height: "80px" }} />
				</Box>
			)}
			<Box>
				<List sx={{ height: "60vh", p: 0 }}>
					{filteredMenuItems.map(({ name, icon, subItems }, index) => {
						const nameKey = typeof name === "string" ? name : `menu-item-${index}`;

						// subItems が1つの場合はサブアイテムを表示しない
						if (subItems && subItems.length === 1) {
							const { path } = subItems[0];
							return (
								<ListItem key={path} disablePadding>
									<ListItemButton
										component={Link}
										to={path}
										selected={pathname === path}
										sx={{ py: 0.5 }}
									>
										<ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>{icon}</ListItemIcon>
										<ListItemText
											primary={
												<Typography color="black" sx={{ whiteSpace: "normal", fontSize: "14px" }}>
													{name}
												</Typography>
											}
										/>
									</ListItemButton>
								</ListItem>
							);
						}

						// subItems が2つ以上ある場合はサブアイテムを表示する
						return (
							<Box key={nameKey}>
								<ListItemButton
									sx={{
										py: 0.5,
										cursor: "default",
									}}
								>
									<ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>{icon}</ListItemIcon>
									<ListItemText>
										<Typography color="black" sx={{ fontSize: "14px" }}>
											{name}
										</Typography>
									</ListItemText>
								</ListItemButton>

								<Collapse in={true} timeout="auto" unmountOnExit>
									<List component="div" disablePadding>
										{subItems?.map(({ text, path }) => (
											<ListItem key={path} disablePadding>
												<ListItemButton
													component={Link}
													to={path}
													selected={pathname === path}
													sx={{ pl: 8, pt: 0.5, pb: 0.5 }}
												>
													<ListItemText
														primary={
															<Typography
																sx={{
																	fontSize: "13px",
																	whiteSpace: "normal",
																}}
															>
																{text}
															</Typography>
														}
													/>
												</ListItemButton>
											</ListItem>
										))}
									</List>
								</Collapse>
							</Box>
						);
					})}
				</List>
			</Box>
		</Drawer>
	);
}
